import {
  AwaitContainer,
  Button,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  ScrollArea,
} from '@ibsys/kit';
import { HistoryIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getVerificationStatusLabel, useExpertReportVerificationStatusHistory } from '@/entities/expert-report';
import { formatHumanDate, SCROLL_AREA_VIEWPORT_BLOCK } from '@/shared/lib';
import { IconButton } from '@/shared/ui';

type Props = {
  conversationId: number;
  expertReportId: number;
};

/**
 * История смен статуса проверки результата заключения: откуда, куда, кто сменил и когда.
 *
 * Открыта каждому, кто видит диалог, — правка статуса тут ни при чём. Смену, сделанную расчётом
 * заключения, никто не делал, поэтому вместо автора у неё назван сам расчёт.
 */
export const ExpertReportVerificationStatusHistory: FC<Props> = ({ conversationId, expertReportId }) => {
  const { t, i18n } = useTranslation(['dialogs']);
  const [isOpen, setIsOpen] = useState(false);
  // История нужна только открытой: у каждого заключения диалога она своя, а смотрят её заметно
  // реже, чем сам результат
  const { data, isLoading } = useExpertReportVerificationStatusHistory(conversationId, expertReportId, isOpen);
  const title = t('dialogs:expertReport.verificationStatusHistory');
  const items = data?.items ?? [];

  return (
    <DialogRoot open={isOpen} onOpenChange={setIsOpen}>
      <IconButton
        size="icon"
        className="size-5 align-middle"
        label={title}
        icon={<HistoryIcon className="size-3" />}
        trigger={DialogTrigger}
      />
      {/* Своей высотой окно kit за экран не держится, а смен статуса у результата копится сколько
          угодно: потолок высоты и прокрутка перечня — здесь. Высота считается по видимой части
          экрана: на телефоне её занижает адресная строка браузера */}
      <DialogContent className="max-h-[calc(100dvh_-_2rem)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <ScrollArea orientation="vertical" className={SCROLL_AREA_VIEWPORT_BLOCK}>
          <AwaitContainer isLoading={isLoading}>
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('dialogs:expertReport.verificationStatusHistoryEmpty')}
              </p>
            ) : (
              <div className="flex flex-col gap-2 text-sm">
                {items.map((item) => (
                  <div key={`${item.changedAt}-${item.status}`} className="flex flex-col gap-0.5">
                    <span>
                      {`${getVerificationStatusLabel(t, item.previousStatus)} → ${getVerificationStatusLabel(t, item.status)}`}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {`${item.authorName ?? t('dialogs:expertReport.verificationStatusChangedByPipeline')}, ${formatHumanDate(i18n, item.changedAt, { withTime: true })}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </AwaitContainer>
        </ScrollArea>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t('dialogs:expertReport.close')}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
